// src/components/account/AccountTransactions.jsx
import * as React from "react";
import {
  Paper, Typography, Stack, Divider, Button, IconButton,
  CircularProgress, Alert, Collapse, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Box, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery,
  Link, TextField, InputAdornment, Tabs, Tab, Chip, Select, MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckIcon from "@mui/icons-material/Check";

import { api } from "../../lib/api";

// i18n
import { useI18n } from "../../i18n/I18nProvider";
import accountTransactionsI18n from "../../i18n/components/account/accounttransactions.i18n";
import pricelistI18n from "../../i18n/components/tarifs/pricelist.i18n";
import recapI18n from "../../i18n/pages/recappage.i18n";

// toast stylé
import FancyToast from "../common/FancyToast";

const BLUE = "#0466c8";
const BLUE_HOVER = "#004890";
const BLUE_BG = "rgba(11,95,255,0.06)";
const CONTACT_EMAIL = "contact@123back.eu";
const money = (n) => Number(n || 0).toFixed(2);
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

const REQUIRED_PROFILE_FIELDS = ["firstname", "name", "address", "postalCode", "city", "phone"];

/* ===== Utils IBAN ===== */
function isValidIban(ibanRaw = "") {
  const iban = ibanRaw.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let converted = "";
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    if (code >= 65 && code <= 90) converted += (code - 55).toString();
    else converted += ch;
  }
  let remainder = 0;
  for (let i = 0; i < converted.length; i += 7) {
    const part = String(remainder) + converted.slice(i, i + 7);
    remainder = Number(part) % 97;
  }
  return remainder === 1;
}

/* ===== Petit chip de statut ===== */
function StatusChip({ label, active }) {
  return (
    <Typography
      variant="caption"
      sx={{
        px: 1,
        py: 0.2,
        borderRadius: 1,
        fontWeight: 600,
        bgcolor: active ? "rgba(11,95,255,0.1)" : "grey.100",
        color: active ? BLUE : "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  );
}

/* ===== Dialogs IBAN ===== */
function ConfirmIbanDialog({ open, onClose, ibanMasked, onConfirm, onChangeIban, t }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ fontWeight: 900, px: 2, py: 1.5, bgcolor: "rgba(4,102,200,0.06)", borderBottom: "1px solid #e8efe9" }}>
        {t("accountTransactions.iban.confirm.title")}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.75 }}>
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e8efe9", background: "#fff" }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t("accountTransactions.iban.confirm.savedIbanTitle")}</Typography>
          <Typography sx={{ color: "text.secondary" }}>{ibanMasked || "—"}</Typography>
        </Paper>
        <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: 14, lineHeight: 1.6 }}>
          {t("accountTransactions.iban.confirm.body")}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, gap: 1 }}>
        <Button
          onClick={onChangeIban}
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, borderColor: "#dfe7e1", color: "text.primary",
                "&:hover": { borderColor: "#cfd8d2", bgcolor: "rgba(0,0,0,0.02)" } }}
        >
          {t("accountTransactions.iban.confirm.changeIban")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("accountTransactions.common.close")}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: BLUE_HOVER } }}
        >
          {t("accountTransactions.iban.confirm.useThis")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function IbanFormDialog({ open, onClose, onSaved, defaultOwnerName = "", t }) {
  const [iban, setIban] = React.useState("");
  const [ownerName, setOwnerName] = React.useState(defaultOwnerName || "");
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (open) { setIban(""); setErr(""); }
  }, [open]);

  const save = async () => {
    setErr("");
    const trimmed = (iban || "").replace(/\s+/g, "");
    if (!isValidIban(trimmed)) {
      setErr(t("accountTransactions.iban.errors.invalidIban"));
      return;
    }
    try {
      setLoading(true);
      await api.post("/iban/upsert", { iban: trimmed, ownerName: ownerName || undefined });
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.message || t("accountTransactions.iban.errors.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ fontWeight: 900, px: 2, py: 1.5, bgcolor: "rgba(4,102,200,0.06)", borderBottom: "1px solid #e8efe9" }}>
        {t("accountTransactions.iban.form.title")}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.75 }}>
        <Alert severity="info" sx={{ mb: 1.5, bgcolor: BLUE_BG }}>
          {t("accountTransactions.iban.form.info")}
        </Alert>
        <Stack spacing={1.25}>
          <TextField
            label={t("accountTransactions.iban.form.ownerLabel")}
            size="small"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("accountTransactions.iban.form.ibanLabel")}
            size="small"
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            placeholder="PL76 3000 6000 0112 3456 7890 189"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {iban && isValidIban(iban) && <CheckIcon sx={{ color: BLUE }} fontSize="small" />}
                </InputAdornment>
              ),
            }}
            helperText={t("accountTransactions.iban.form.helper")}
            error={!!err}
          />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("accountTransactions.common.close")}
        </Button>
        <Button
          onClick={save}
          disabled={loading}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: BLUE_HOVER } }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : t("accountTransactions.iban.form.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Helper to normalize the item label and append expertise information
function formatItemLabel(it, t) {
  const id = String(it?.id || "");
  const raw = String(it?.label || "");

  const strip = (s = "") => String(s).replace(/\s+/g, " ").trim();

  // ✅ Detect "batch sort" no matter the id prefix (brand:, custom:, empty, etc.)
  const normalizeBatch = (s = "") => {
    try {
      return String(s)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .toLowerCase()
        .replace(/[’']/g, "'")
        .replace(/[^a-z0-9\s!]/g, " ") // keep letters/numbers/spaces/!
        .replace(/\s+/g, " ")
        .trim();
    } catch {
      return String(s).toLowerCase().replace(/\s+/g, " ").trim();
    }
  };

  const lowBatch = normalizeBatch(raw);

  const isBatchSort =
    // FR
    lowBatch.includes("vendez en 1 clic") ||
    lowBatch.includes("envoie") ||
    lowBatch.includes("on trie pour toi") ||
    // EN
    lowBatch.includes("send it") ||
    lowBatch.includes("we'll sort") ||
    lowBatch.includes("well sort") ||
    lowBatch.includes("sort it for you") ||
    // ES
    lowBatch.includes("envialo") ||
    lowBatch.includes("lo clasificamos") ||
    lowBatch.includes("clasificamos por ti") ||
    // PL
    lowBatch.includes("wyslij") ||
    lowBatch.includes("posortujemy") ||
    lowBatch.includes("za ciebie");

  if (isBatchSort) {
    // => will render correctly in current locale (fr/en/pl/es)
    const baseLabel = t("accountTransactions.labels.batchSort");
    return appendExpertiseNote(it, t, baseLabel);
  }

  function normalizeComponentPrefix(rawComp = "") {
    let base = "";
    try {
      base = String(rawComp)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    } catch {
      base = String(rawComp).toLowerCase().trim();
    }

    const fallback = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

    if (!base) return t("recap.labels.screen", "Écran");

    if (base.includes("ecran") || base.includes("screen") || base.includes("lcd") || base.includes("display")) {
      return t("recap.labels.screen", "Écran");
    }

    if (base.includes("carte mere") || base.includes("motherboard") || base.includes("mainboard") || base.includes("logic board")) {
      return t("recap.labels.motherboard", "Carte mère");
    }

    if (base.includes("batterie") || base.includes("battery")) {
      return t("recap.labels.battery", "Batterie");
    }

    if (base.includes("camera") || base.includes("camera module") || base.includes("cam")) {
      return t("recap.labels.camera", "Caméra");
    }

    if ((base.includes("haut") && base.includes("parleur")) || base.includes("speaker") || base.includes("loudspeaker")) {
      return t("recap.labels.speaker", "Haut-parleur");
    }

    if (base.includes("microphone") || base.includes("micro")) {
      return t("recap.labels.microphone", "Micro");
    }

    if (base.includes("connecteur") || base.includes("charge") || base.includes("charging") || base.includes("dock") || base.includes("usb")) {
      return t("recap.labels.chargingConnector", "Connecteur de charge");
    }

    if (base.includes("bouton") || base.includes("button")) {
      return t("recap.labels.button", "Bouton");
    }

    if (base.includes("vibreur") || base.includes("taptic") || base.includes("vibrator")) {
      return t("recap.labels.vibrator", "Vibreur");
    }

    return fallback(strip(rawComp));
  }

  // --- Base label formatting: same rules as RecapPage ---
  const parts = raw.includes("—") ? raw.split("—").map(strip) : [strip(raw)];
  const screenLabel = t("recap.labels.screen", "Écran");

  let baseLabel;

  if (id.startsWith("model:")) {
    const left = parts[0] || raw.replace(/—\s*Appareil\s+complet$/i, "").trim();
    baseLabel = `${screenLabel} — ${left}`;
  } else if (id.startsWith("component:")) {
    if (parts.length >= 2) {
      const leftBrandModel = parts[0];
      const rightComponent = parts.slice(1).join(" — ");
      const prefix = normalizeComponentPrefix(rightComponent);
      baseLabel = `${prefix} — ${leftBrandModel}`;
    } else {
      baseLabel = raw;
    }
  } else if (id.startsWith("brand:")) {
    // ✅ Ajout du préfixe (Écran / Screen / etc.) comme dans le récap
    const label = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
    baseLabel = `${screenLabel} — ${label}`;
  } else {
    // ✅ Fallback quand l'id ne contient pas model:/component:/brand:
    // - "Apple iphone 16 pro" => Écran — Apple iphone 16 pro
    // - "Apple IPHONE 13 — connecteur charge" => Connecteur de charge — Apple IPHONE 13
    // - "... — Appareil complet" => Écran — ...

    // Cas 1 : pas de séparateur => écran
    if (!raw.includes("—")) {
      const left = strip(raw);
      baseLabel = left ? `${screenLabel} — ${left}` : screenLabel;
    }
    // Cas 2 : "— Appareil complet" => écran
    else if (raw.match(/—\s*Appareil\s+complet\s*$/i)) {
      const left = strip(raw.replace(/—\s*Appareil\s+complet\s*$/i, ""));
      baseLabel = left ? `${screenLabel} — ${left}` : screenLabel;
    }
    // Cas 3 : "Brand Model — component" => composant devant
    else if (parts.length >= 2) {
      const leftBrandModel = parts[0];
      const rightComponent = parts.slice(1).join(" — ");
      const prefix = normalizeComponentPrefix(rightComponent);
      baseLabel = `${prefix} — ${leftBrandModel}`;
    } else {
      baseLabel = raw;
    }
  }

  return appendExpertiseNote(it, t, baseLabel);
}

// ✅ factorized note suffix (unchanged behavior)
function appendExpertiseNote(it, t, baseLabel) {
  const note = it?.expertiseNote;
  if (note == null) return baseLabel;

  const packagesLabel =
    t("accountTransactions.meta.packages") || t("recap.labels.packages", "colis");
  const piecesLabel =
    t("accountTransactions.meta.pieces") || t("recap.labels.pieces", "pièces");
  const kgLabel = "kg";

  if (typeof note === "object") {
    const packagesRaw =
      note.parcelsCount ??
      note.parcels ??
      note.packagesCount ??
      note.packages ??
      note.colis;

    const weightRaw = note.weightKg ?? note.weight;
    const piecesRaw = note.piecesCount ?? note.pieces;

    const piecesStr =
      piecesRaw != null && String(piecesRaw).trim() !== ""
        ? `${String(piecesRaw).trim()} ${piecesLabel}`
        : "";

    const weightStr =
      weightRaw != null && String(weightRaw).trim() !== ""
        ? `${String(weightRaw).trim()} ${kgLabel}`
        : "";

    const packagesStr =
      packagesRaw != null && String(packagesRaw).trim() !== ""
        ? `${String(packagesRaw).trim()} ${packagesLabel}`
        : "";

    const partsNote = [piecesStr, weightStr, packagesStr].filter(Boolean);

    if (partsNote.length > 0) {
      return `${baseLabel} (${partsNote.join(" · ")})`;
    }
  }

  const trimmed = String(note).trim();
  if (trimmed) return `${baseLabel} (${trimmed})`;

  return baseLabel;
}
export default function AccountTransactions() {
  const { t, register } = useI18n();
  React.useEffect(() => {
    register("accountTransactions", accountTransactionsI18n);
    register("tarifs", pricelistI18n);
    register("recap", recapI18n);
  }, [register]);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [openIds, setOpenIds] = React.useState(() => new Set());

  // Filtres / tri
  const [statusFilter, setStatusFilter] = React.useState("all"); // all | ongoing | past
  const [sortBy, setSortBy] = React.useState("newest"); // newest | oldest | amountDesc | amountAsc

  // PDF recap (contrôle)
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState("");
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfErr, setPdfErr] = React.useState("");
  const [pdfFileName, setPdfFileName] = React.useState("verification.pdf");

  // Waybill (transport)
  const [wbOpen, setWbOpen] = React.useState(false);
  const [wbBlobUrl, setWbBlobUrl] = React.useState("");
  const [wbLoading, setWbLoading] = React.useState(false);
  const [wbErr, setWbErr] = React.useState("");
  const [wbFileName, setWbFileName] = React.useState("transport.pdf");

  // ✨ Décision client (après récap PDF)
  const [decisionDialogOpen, setDecisionDialogOpen] = React.useState(false);
  const [decisionTx, setDecisionTx] = React.useState(null);
  const [decisionLoading, setDecisionLoading] = React.useState(false);
  const [decisionErr, setDecisionErr] = React.useState("");
  const [pendingDecision, setPendingDecision] = React.useState(null);
  // nouvelle popup d'avertissement en cas de refus
  const [rejectWarningOpen, setRejectWarningOpen] = React.useState(false);

  // IBAN state
  const [ibanConfirmOpen, setIbanConfirmOpen] = React.useState(false);
  const [ibanFormOpen, setIbanFormOpen] = React.useState(false);
  const [ibanMasked, setIbanMasked] = React.useState("—");
  const [ownerName, setOwnerName] = React.useState("");
  const [ibanToastOpen, setIbanToastOpen] = React.useState(false);
  const [hasIban, setHasIban] = React.useState(false);

  // Profil utilisateur
  const [profile, setProfile] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [profileErr, setProfileErr] = React.useState("");

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const load = React.useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.get("/transactions");
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRows(list);
    } catch (e) {
      setErr(e?.response?.data?.message || t("accountTransactions.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadProfile = async () => {
    setProfileErr("");
    setProfileLoading(true);
    try {
      const { data } = await api.get("/users/me");
      setProfile(data || null);
    } catch (e) {
      setProfileErr(e?.response?.data?.message || "");
    } finally {
      setProfileLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Charger l'état IBAN pour griser le bouton si déjà enregistré
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/iban/me"); // { hasIban, ibanMasked, ownerName? }
        setHasIban(!!data?.hasIban);
        setIbanMasked(data?.ibanMasked || "—");
        setOwnerName(data?.ownerName || "");
      } catch {
        // ignore
      }
    })();
  }, []);

  /* ===== Calcul profil incomplet ===== */
  const missingProfileFields = React.useMemo(() => {
    if (!profile) return [];
    return REQUIRED_PROFILE_FIELDS.filter((k) => {
      const v = profile[k];
      return v === null || v === undefined || String(v).trim() === "";
    });
  }, [profile]);

  const showProfileBanner =
    !profileLoading &&
    !profileErr &&
    profile &&
    rows.length > 0 &&
    missingProfileFields.length > 0;

  /* ===== OPEN PDFs ===== */
  const openPdf = async (tRow) => {
    const code = String(tRow?.id || "").slice(0, 8);
    setPdfFileName(`${t("accountTransactions.files.shipment")}-${code}.pdf`);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl("");
    }
    setPdfErr("");
    setPdfLoading(true);
    setPdfOpen(true);
    try {
      const resp = await api.get(`/transactions/${tRow.id}/verification.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPdfErr(e?.response?.data?.message || t("accountTransactions.pdf.unavailable"));
    } finally {
      setPdfLoading(false);
    }
  };
  const closePdf = () => {
    setPdfOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl("");
    }
  };
  const downloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = pdfFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openWaybillPdf = async (tRow) => {
    const code = String(tRow?.id || "").slice(0, 8);
    setWbFileName(`${t("accountTransactions.files.waybill")}-${code}.pdf`);
    if (wbBlobUrl) {
      URL.revokeObjectURL(wbBlobUrl);
      setWbBlobUrl("");
    }
    setWbErr("");
    setWbLoading(true);
    setWbOpen(true);
    try {
      const resp = await api.get(`/transactions/${tRow.id}/waybill.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
      setWbBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setWbErr(e?.response?.data?.message || t("accountTransactions.waybill.unavailable"));
    } finally {
      setWbLoading(false);
    }
  };
  const closeWaybill = () => {
    setWbOpen(false);
    if (wbBlobUrl) {
      URL.revokeObjectURL(wbBlobUrl);
      setWbBlobUrl("");
    }
  };
  const downloadWaybill = () => {
    if (!wbBlobUrl) return;
    const a = document.createElement("a");
    a.href = wbBlobUrl;
    a.download = wbFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  React.useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      if (wbBlobUrl) URL.revokeObjectURL(wbBlobUrl);
    };
  }, [pdfBlobUrl, wbBlobUrl]);

  // Montants pour la popup de décision (offre)
  const decisionEstimated = decisionTx ? Number(decisionTx.priceEstimate || 0) : 0;
  const decisionBuyback = decisionTx ? Number(decisionTx.totalBuyback || 0) : 0;
  const decisionPaidAmount = decisionTx ? Number(decisionTx.paymentAmount || 0) : 0;
  const decisionValidated = decisionTx ? (decisionBuyback || decisionPaidAmount || decisionEstimated) : 0;

  // Helpers responsive: bouton texte sur desktop / icône sur mobile
  const ActionBtn = ({ title, icon, onClick, disabled = false }) => (
    isSm ? (
      <Tooltip title={title}>
        <span>
          <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{ color: disabled ? "text.disabled" : BLUE, opacity: disabled ? 0.6 : 1 }}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    ) : (
      <Button
        size="small"
        variant="outlined"
        startIcon={icon}
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderColor: disabled ? "rgba(0,0,0,0.12)" : BLUE,
          color: disabled ? "text.disabled" : BLUE,
          opacity: disabled ? 0.6 : 1,
          "&:hover": { borderColor: disabled ? "rgba(0,0,0,0.12)" : BLUE_HOVER, color: disabled ? "text.disabled" : BLUE_HOVER },
        }}
      >
        {title}
      </Button>
    )
  );

  const mailHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t("accountTransactions.contact.subject"))}`;

  /* ===== Flow IBAN (depuis la liste) ===== */
  const startIbanFlow = async () => {
    try {
      const { data } = await api.get("/iban/me"); // { hasIban, ibanMasked, ownerName? }
      const has = !!data?.hasIban;
      setHasIban(has);
      setOwnerName(data?.ownerName || "");
      if (has) {
        setIbanMasked(data?.ibanMasked || "—");
        setIbanConfirmOpen(true);
      } else {
        setIbanFormOpen(true);
      }
    } catch {
      setIbanFormOpen(true);
    }
  };

  const confirmUseExistingIban = () => {
    setIbanConfirmOpen(false);
    setIbanToastOpen(true);
  };

  const onIbanSavedThenToast = async () => {
    setIbanFormOpen(false);
    setHasIban(true);

    if (pendingDecision) {
      try {
        setDecisionLoading(true);
        await api.patch(`/transactions/${pendingDecision.txId}/decision`, { decision: pendingDecision.choice });
        setPendingDecision(null);
        await load();
      } catch (e) {
        setDecisionErr(e?.response?.data?.message || t("accountTransactions.decision.error"));
      } finally {
        setDecisionLoading(false);
      }
    }

    setIbanToastOpen(true);
  };

  /* ===== Flow décision client (après récap PDF) ===== */
  const startDecision = (tRow) => {
    setDecisionTx(tRow);
    setDecisionErr("");
    setDecisionDialogOpen(true);
  };

  const submitDecision = async (choice) => {
    if (!decisionTx) return;
    setDecisionErr("");

    // Si l'utilisateur accepte l'offre mais n'a pas d'IBAN, on ouvre d'abord le flux IBAN
    if (choice === "accepted" && !hasIban) {
      setPendingDecision({ txId: decisionTx.id, choice });
      setDecisionDialogOpen(false);
      setRejectWarningOpen(false);
      setDecisionTx(null);
      startIbanFlow();
      return;
    }

    setDecisionLoading(true);
    try {
      await api.patch(`/transactions/${decisionTx.id}/decision`, { decision: choice });
      setDecisionDialogOpen(false);
      setRejectWarningOpen(false);
      setDecisionTx(null);
      setPendingDecision(null);
      await load();
    } catch (e) {
      setDecisionErr(e?.response?.data?.message || t("accountTransactions.decision.error"));
    } finally {
      setDecisionLoading(false);
    }
  };

  // Handler pour modifier une transaction inactive
  const handleEditTransaction = (tx) => {
    if (!tx) return;

    // On envoie les lignes de la transaction vers /tarifs
    // tx.items doit contenir { label, unitPrice, qty, ... }
    const payload = {
      transactionId: tx.id,
      items: Array.isArray(tx.items) ? tx.items : [],
    };

    navigate("/tarifs", {
      state: {
        from: "account-transactions",
        restoreFromTransaction: payload,
        editingTransactionId: tx.id, // 👈 très important : on garde l'id à éditer
      },
    });
  };

  // Liste filtrée + triée
  const filteredSortedRows = React.useMemo(() => {
    let list = Array.isArray(rows) ? [...rows] : [];

    if (statusFilter === "ongoing") list = list.filter(r => !r?.statusPayment);
    if (statusFilter === "past") list = list.filter(r => !!r?.statusPayment);

    list.sort((a, b) => {
      const totalA = Number(a?.totalBuyback ?? a?.paymentAmount ?? a?.priceEstimate ?? 0);
      const totalB = Number(b?.totalBuyback ?? b?.paymentAmount ?? b?.priceEstimate ?? 0);
      const dateA = new Date(a?.createdAt || 0).getTime();
      const dateB = new Date(b?.createdAt || 0).getTime();

      switch (sortBy) {
        case "oldest":     return dateA - dateB;
        case "amountDesc": return totalB - totalA;
        case "amountAsc":  return totalA - totalB;
        case "newest":
        default:           return dateB - dateA;
      }
    });

    return list;
  }, [rows, statusFilter, sortBy]);

  // Retention info: when a transaction is canceled/paused, it will be deleted after N days
  const getRetentionInfo = React.useCallback((tx) => {
    if (!tx?.canceledAt) return null;

    const RETENTION_DAYS = 45;

    const canceledAt = new Date(tx.canceledAt);
    if (Number.isNaN(canceledAt.getTime())) return null;

    const deleteAt = new Date(canceledAt);
    deleteAt.setDate(deleteAt.getDate() + RETENTION_DAYS);

    const now = new Date();
    const diffMs = deleteAt.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

    return {
      deleteAtLabel: deleteAt.toLocaleDateString("fr-FR"),
      daysLeft,
    };
  }, []);
  /* ===== Rendu ===== */
  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2, gap: 1 }}
      >
        <Typography variant={isSm ? "subtitle1" : "h6"} sx={{ fontWeight: 800 }}>
          {t("accountTransactions.title")}
        </Typography>
        <Tooltip title={t("accountTransactions.actions.refresh")}>
          <span>
            <IconButton onClick={load} disabled={loading} size={isSm ? "small" : "medium"}>
              <RefreshIcon fontSize={isSm ? "small" : "medium"} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* 🔴 Bannière profil incomplet (uniquement si au moins 1 vente) */}
      {showProfileBanner && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: "#ffebee",
            border: "1px solid #ef9a9a",
            display: "flex",
            alignItems: "center",
          }}
          action={
            <Button
              component={RouterLink}
              to="compte"
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#c62828",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              {t("accountTransactions.profileMissing.button")}
            </Button>
          }
        >
          {t("accountTransactions.profileMissing.text")}
        </Alert>
      )}

      {/* Bandeau contact */}
      <Alert
        icon={<MailOutlineIcon />}
        severity="info"
        sx={{
          mb: 2,
          bgcolor: BLUE_BG,
          color: BLUE,
          "& .MuiAlert-icon": { color: BLUE },
          alignItems: "center",
        }}
        action={
          <Button size="small" href={mailHref} sx={{ color: BLUE }}>
            {t("accountTransactions.contact.button")}
          </Button>
        }
      >
        {t("accountTransactions.contact.text")}&nbsp;
        <Link href={mailHref} underline="hover" sx={{ fontWeight: 700, color: BLUE }}>
          {CONTACT_EMAIL}
        </Link>
      </Alert>

      {/* Filtres + tri */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2, gap: 1 }}
      >
        <Tabs
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": { textTransform: "none", minHeight: 36, px: 1.5 },
            "& .MuiTabs-indicator": { bgcolor: BLUE },
          }}
        >
          <Tab value="all" label={t("accountTransactions.filters.all")} />
          <Tab value="ongoing" label={t("accountTransactions.filters.ongoing")} />
          <Tab value="past" label={t("accountTransactions.filters.past")} />
        </Tabs>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("accountTransactions.filters.sortLabel")}
          </Typography>
          <Select
            size="small"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="newest">{t("accountTransactions.filters.sort.newest")}</MenuItem>
            <MenuItem value="oldest">{t("accountTransactions.filters.sort.oldest")}</MenuItem>
            <MenuItem value="amountDesc">{t("accountTransactions.filters.sort.amountDesc")}</MenuItem>
            <MenuItem value="amountAsc">{t("accountTransactions.filters.sort.amountAsc")}</MenuItem>
          </Select>
        </Stack>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={18} /> {t("accountTransactions.loading")}
        </Stack>
      ) : filteredSortedRows.length === 0 ? (
        rows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 3,
              textAlign: "center",
              bgcolor: "#f8fbff",
              borderColor: "#dfeafb",
            }}
          >
            <Stack spacing={1.25} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {t("accountTransactions.emptyCta.title")}
              </Typography>
              <Typography sx={{ color: "text.secondary", maxWidth: 520 }}>
                {t("accountTransactions.emptyCta.subtitle")}
              </Typography>
              <Button
                component={RouterLink}
                to="/tarifs"
                variant="contained"
                sx={{ mt: 0.5, textTransform: "none", fontWeight: 800, bgcolor: BLUE, "&:hover": { bgcolor: BLUE_HOVER } }}
              >
                {t("accountTransactions.emptyCta.button")}
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              textAlign: "center",
              bgcolor: "#f8fbff",
              borderColor: "#dfeafb",
            }}
          >
            <Stack spacing={1.25} alignItems="center">
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("accountTransactions.filters.emptyAfterFilter")}
              </Typography>
            </Stack>
          </Paper>
        )
      ) : (
        <Stack spacing={2}>
          {filteredSortedRows.map((tRow) => {
            const created = tRow?.createdAt ? new Date(tRow.createdAt).toLocaleString() : "—";
            const total = Number(tRow?.priceEstimate) || 0;
            const paid = Number(tRow?.paymentAmount) || 0;
            const buyback = Number(tRow?.totalBuyback) || 0;
            const isOpen = openIds.has(tRow.id);

            const hasPdf = !!tRow?.statusPdfRecap;
            const hasWaybill = !!tRow?.statusWaybillCreated;
            const isPaid = !!tRow?.statusPayment;

            const decision = tRow?.decision || null;
            let decisionLabel = "";
            let decisionColor = "default";

            if (decision === "accepted") {
              decisionLabel = t("accountTransactions.decision.status.accepted");
              decisionColor = "success";
            } else if (decision === "rejected") {
              decisionLabel = t("accountTransactions.decision.status.rejected");
              decisionColor = "error";
            } else if (decision === "accepted_with_return") {
              decisionLabel = t("accountTransactions.decision.status.acceptedWithReturn");
              decisionColor = "info";
            }

            const isInactive = tRow?.isActive === false;
            const retention = getRetentionInfo(tRow);
            const showRetentionMsg = !!retention && !tRow?.isDeleted;

            return (
              <Paper
                key={tRow.id}
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  position: "relative",
                  overflow: "hidden",
                  borderColor: isInactive ? "#ffb74d" : "divider",
                  bgcolor: isInactive ? "#fff8e1" : "background.paper",
                  transition:
                    "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                  boxShadow: isInactive ? "0 0 0 1px rgba(255,183,77,0.6)" : undefined,
                }}
              >
                {/* Contenu normal flouté si transaction annulée */}
                <Box
                  sx={{
                    filter: isInactive ? "blur(2px)" : "none",
                    pointerEvents: isInactive ? "none" : "auto",
                    transition: "filter 0.2s ease",
                  }}
                >
                  {!hasWaybill && (
                    <Alert
                      severity="info"
                      sx={{
                        mb: 1.25,
                        bgcolor: BLUE_BG,
                        color: BLUE,
                        "& .MuiAlert-icon": { color: BLUE },
                        fontSize: { xs: 12, sm: 14 },
                        "& .MuiAlert-message": { width: "100%" },
                      }}
                    >
                      {t("accountTransactions.info.waybillPending")}
                    </Alert>
                  )}

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ gap: 1.25 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {t("accountTransactions.labels.shipment")} #{String(tRow.numPackage)}
                        </Typography>

                        {isPaid && (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckIcon />}
                            label={t("accountTransactions.badges.paid")}
                            sx={{
                              height: 22,
                              "& .MuiChip-label": { px: 0.75, fontWeight: 700 },
                            }}
                          />
                        )}
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {t("accountTransactions.labels.createdAt")} {created}
                      </Typography>

                      {tRow?.shippingTrackingNumber && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ mt: 0.25, minWidth: 0 }}
                        >
                          <LocalShippingIcon
                            fontSize="small"
                            sx={{ color: BLUE, flexShrink: 0 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.4,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={tRow.shippingTrackingNumber}
                          >
                            <b>{t("accountTransactions.labels.tracking")}:</b>{" "}
                            {tRow.shippingTrackingNumber}
                          </Typography>
                        </Stack>
                      )}
                      {tRow?.returnTrackingNumber && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ mt: 0.25, minWidth: 0 }}
                        >
                          <LocalShippingIcon
                            fontSize="small"
                            sx={{ color: BLUE, flexShrink: 0 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.4,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={tRow.returnTrackingNumber}
                          >
                            <b>{t("accountTransactions.labels.trackingReturn")}:</b>{" "}
                            {tRow.returnTrackingNumber}
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {hasWaybill && (
                        <ActionBtn
                          title={t("accountTransactions.buttons.waybill")}
                          icon={<LocalShippingIcon />}
                          onClick={() => openWaybillPdf(tRow)}
                        />
                      )}

                      {hasPdf && (
                        <ActionBtn
                          title={t("accountTransactions.buttons.pdfRecap")}
                          icon={<ReceiptLongIcon />}
                          onClick={() => openPdf(tRow)}
                        />
                      )}

                      {hasPdf && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => startDecision(tRow)}
                          disabled={!!decision}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: BLUE,
                            "&:hover": { bgcolor: BLUE_HOVER },
                            px: 1.5,
                          }}
                        >
                          {decision
                            ? t("accountTransactions.decision.buttonDone")
                            : t("accountTransactions.decision.button")}
                        </Button>
                      )}

                      <Button
                        size="small"
                        onClick={() => toggle(tRow.id)}
                        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{
                          color: BLUE,
                          "&:hover": { bgcolor: BLUE_BG },
                          alignSelf: { xs: "flex-start", md: "center" },
                        }}
                      >
                        {isOpen
                          ? t("accountTransactions.buttons.hide")
                          : t("accountTransactions.buttons.details")}
                      </Button>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.25 }} />

                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    rowGap={1}
                    sx={{ mb: 1, "& > *": { mr: 1 } }}
                  >
                    <Typography>
                      {t("accountTransactions.labels.total")} {money(total)} €
                    </Typography>
                    {buyback > 0 && tRow?.statusPdfRecap ? (
                      <Typography>
                        {t("accountTransactions.labels.buyback")} {money(buyback)} €
                      </Typography>
                    ) : (
                      <Typography>
                        {t("accountTransactions.labels.paid")} {money(paid)} €
                      </Typography>
                    )}
                    <Tooltip
                      title={`${t("accountTransactions.status.sentAt")} ${fmt(
                        tRow?.statusWaybillCreatedAt
                      )}`}
                    >
                      <span>
                        <StatusChip
                          label={t("accountTransactions.status.sent")}
                          active={!!tRow?.statusWaybillCreated}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={`${t("accountTransactions.status.receivedAt")} ${fmt(
                        tRow?.receivedAt
                      )}`}
                    >
                      <span>
                        <StatusChip
                          label={t("accountTransactions.status.received")}
                          active={!!tRow?.statusReception}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={`${t("accountTransactions.status.qualityCheckedAt")} ${fmt(
                        tRow?.qualityCheckedAt
                      )}`}
                    >
                      <span>
                        <StatusChip
                          label={t("accountTransactions.status.quality")}
                          active={!!tRow?.statusControlQuality}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={`${t("accountTransactions.status.pdfRecapAt")} ${fmt(
                        tRow?.pdfRecapAt
                      )}`}
                    >
                      <span>
                        <StatusChip
                          label={t("accountTransactions.status.pdf")}
                          active={!!tRow?.statusPdfRecap}
                        />
                      </span>
                    </Tooltip>

                    {decision && (
                      <Chip
                        size="small"
                        color={decisionColor}
                        label={decisionLabel}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          borderRadius: 1.5,
                          textTransform: "none",
                        }}
                      />
                    )}

                    {decision !== "rejected" && (
                      <Tooltip
                        title={`${t("accountTransactions.status.paidAt")} ${fmt(
                          tRow?.paidAt
                        )}`}
                      >
                        <span>
                          <StatusChip
                            label={t("accountTransactions.status.paid")}
                            active={!!tRow?.statusPayment}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Stack>

                  <Collapse in={isOpen}>
                    <TableContainer sx={{ mt: 1.5, width: "100%", overflowX: "auto" }}>
                      <Table size="small" sx={{ minWidth: 480 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {t("accountTransactions.table.item")}
                            </TableCell>
                            {!isSm && (
                              <TableCell align="right">
                                {t("accountTransactions.table.unitPrice")}
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {t("accountTransactions.table.qty")}
                            </TableCell>
                            <TableCell align="right">
                              {t("accountTransactions.table.total")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tRow.items?.map((it, idx) => {
                            const pu = Number(it.unitPrice || 0);
                            const q = Number(it.qty || 0);
                            const fullLabel = formatItemLabel(it, t);
                            return (
                              <TableRow key={`${tRow.id}-${idx}`}>
                                <TableCell
                                  sx={{
                                    maxWidth: 260,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={fullLabel}
                                >
                                  {fullLabel}
                                </TableCell>
                                {!isSm && (
                                  <TableCell align="right">{money(pu)}</TableCell>
                                )}
                                <TableCell align="right">{q}</TableCell>
                                <TableCell align="right">
                                  <b>{money(pu * q)}</b>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
                {/* 💡 Overlay transaction annulée : message + bouton "Modifier la transaction" */}
                {isInactive && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      textAlign: "center",
                      pointerEvents: "auto",
                      bgcolor: "rgba(255, 224, 178, 0.55)",
                    }}
                  >
                    <Stack
                      spacing={1.25}
                      alignItems="center"
                      sx={{ maxWidth: 480 }}
                    >
                      <Typography
                        sx={{ fontWeight: 800, color: "#e65100" }}
                      >
                        {t("accountTransactions.inactive.title")}
                      </Typography>
                      {showRetentionMsg && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#6d4c41",
                            backgroundColor: "rgba(255, 243, 224, 0.95)",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            boxShadow: "0 0 0 1px rgba(230,81,0,0.25)",
                          }}
                        >
                          {t("accountTransactions.inactive.retentionPrefix")} {" "}
                          <b>{retention.deleteAtLabel}</b> ({retention.daysLeft} {retention.daysLeft > 1
                            ? t("accountTransactions.inactive.days")
                            : t("accountTransactions.inactive.day")} {retention.daysLeft > 1
                            ? t("accountTransactions.inactive.remainingPlural")
                            : t("accountTransactions.inactive.remaining")})
                        </Typography>
                      )}

                      {tRow.inactiveComment && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#5d4037",
                            backgroundColor: "rgba(255, 243, 224, 0.95)",
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            boxShadow: "0 0 0 1px rgba(230,81,0,0.35)",
                          }}
                        >
                          {tRow.inactiveComment}
                        </Typography>
                      )}

                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleEditTransaction(tRow)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                          bgcolor: "#e65100",
                          "&:hover": { bgcolor: "#bf360c" },
                          mt: 0.5,
                        }}
                      >
                        {t("accountTransactions.inactive.editButton")}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Modal — PDF récap (contrôle) */}
      <Dialog
        open={pdfOpen}
        onClose={closePdf}
        fullWidth
        fullScreen={isSm}
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: isSm ? 0 : 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t("accountTransactions.pdf.title")}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ width: "100%", height: isSm ? "100vh" : "80vh", bgcolor: "#f6fbf7" }}>
            {pdfLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                <CircularProgress size={18} />
                <Typography>{t("accountTransactions.pdf.loading")}</Typography>
              </Stack>
            ) : pdfErr ? (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Alert severity="error">{pdfErr}</Alert>
              </Stack>
            ) : pdfBlobUrl ? (
              <iframe title="verification-pdf" src={pdfBlobUrl} style={{ width: "100%", height: "100%", border: 0 }} />
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Typography>{t("accountTransactions.pdf.unavailable")}</Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          {pdfBlobUrl && (
            <>
              <Button startIcon={<DownloadIcon />} onClick={downloadPdf} sx={{ color: BLUE }}>
                {t("accountTransactions.common.download")}
              </Button>
              <Button
                startIcon={<OpenInNewIcon />}
                href={pdfBlobUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: BLUE }}
              >
                {t("accountTransactions.common.openInTab")}
              </Button>
            </>
          )}
          <Button onClick={closePdf}>{t("accountTransactions.common.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal — Transport */}
      <Dialog
        open={wbOpen}
        onClose={closeWaybill}
        fullWidth
        fullScreen={isSm}
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: isSm ? 0 : 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t("accountTransactions.waybill.title")}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ width: "100%", height: isSm ? "100vh" : "80vh", bgcolor: "#f6fbf7" }}>
            {wbLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                <CircularProgress size={18} />
                <Typography>{t("accountTransactions.waybill.loading")}</Typography>
              </Stack>
            ) : wbErr ? (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Alert severity="error">{wbErr}</Alert>
              </Stack>
            ) : wbBlobUrl ? (
              <iframe title="order-pdf" src={wbBlobUrl} style={{ width: "100%", height: "100%", border: 0 }} />
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Typography>{t("accountTransactions.waybill.unavailable")}</Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          {wbBlobUrl && (
            <>
              <Button startIcon={<DownloadIcon />} onClick={downloadWaybill} sx={{ color: BLUE }}>
                {t("accountTransactions.common.download")}
              </Button>
              <Button
                startIcon={<OpenInNewIcon />}
                href={wbBlobUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: BLUE }}
              >
                {t("accountTransactions.common.openInTab")}
              </Button>
            </>
          )}
          <Button onClick={closeWaybill}>{t("accountTransactions.common.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* ✨ Décision client après récap PDF */}
      <Dialog
        open={decisionDialogOpen}
        onClose={decisionLoading ? undefined : () => { setRejectWarningOpen(false); setDecisionDialogOpen(false); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>
          {t("accountTransactions.decision.dialogTitle")}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          {decisionErr && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {decisionErr}
            </Alert>
          )}
          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            {t("accountTransactions.decision.processingNotice")}
          </Typography>
          <Stack spacing={1.25}>
            {/* Option 1 : accepter l'offre */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => submitDecision("accepted")}
              disabled={decisionLoading}
              startIcon={<CheckIcon />}
              sx={{
                justifyContent: "flex-start",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: BLUE,
                "&:hover": { bgcolor: BLUE_HOVER },
                py: 1.4,
              }}
            >
              <Stack alignItems="flex-start" spacing={0.25}>
                <Typography sx={{ fontWeight: 800 }}>
                  {t("accountTransactions.decision.options.accept.title")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t("accountTransactions.decision.options.accept.desc")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  {t("accountTransactions.labels.buyback")} {money(decisionValidated)} €
                </Typography>
              </Stack>
            </Button>

          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            onClick={() => { setRejectWarningOpen(false); setDecisionDialogOpen(false); }}
            disabled={decisionLoading}
            sx={{ textTransform: "none" }}
          >
            {t("accountTransactions.common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔴 2ᵉ popup — avertissement après choix "refus" */}
      <Dialog
        open={rejectWarningOpen}
        onClose={decisionLoading ? undefined : () => setRejectWarningOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>
          {t("accountTransactions.decision.rejectWarning.title")}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography sx={{ color: "text.secondary" }}>
            {t("accountTransactions.decision.rejectWarning.message")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            onClick={() => setRejectWarningOpen(false)}
            disabled={decisionLoading}
            sx={{ textTransform: "none" }}
          >
            {t("accountTransactions.decision.rejectWarning.back")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // validation finale du refus
              submitDecision("rejected");
            }}
            disabled={decisionLoading}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: BLUE_HOVER } }}
          >
            {t("accountTransactions.decision.rejectWarning.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔐 Popups IBAN */}
      <ConfirmIbanDialog
        open={ibanConfirmOpen}
        onClose={() => setIbanConfirmOpen(false)}
        ibanMasked={ibanMasked}
        onConfirm={confirmUseExistingIban}
        onChangeIban={() => { setIbanConfirmOpen(false); setIbanFormOpen(true); }}
        t={t}
      />
      <IbanFormDialog
        open={ibanFormOpen}
        onClose={() => setIbanFormOpen(false)}
        onSaved={onIbanSavedThenToast}
        defaultOwnerName={ownerName}
        t={t}
      />

      {/* 🎉 Toast IBAN enregistré */}
      <FancyToast
        open={ibanToastOpen}
        onClose={() => setIbanToastOpen(false)}
        kind="success"
        title={t("accountTransactions.iban.toast.title")}
        subtitle={t("accountTransactions.iban.toast.subtitle")}
        autoHideDuration={1400}
      />
    </Paper>
  );
}
